import React, {
    createContext,
    use,
    useCallback,
    useEffect,
    useMemo,
    useState,
    type FC,
    type PropsWithChildren,
} from "react";
import { type DataSnapshot, child } from "firebase/database";
import type { GQL } from "@binale-tech/shared";

import type { Payment } from "@domain/models/Payment";
import { readFirebasePayment } from "@domain/models/converters/firebaseReaders";
import { logDatasetSize } from "@domain/utils/datasetSizeLogger";
import { CompanyContext } from "@domain/context/CompanyContext";
import { paymentsSave, paymentsSavePreflight } from "@domain/context/accountingData/mutations/paymentMutations.graphql";
import { refPayments } from "@domain/api/firebase/firebaseRootRefs";
import { useMutation } from "@apollo/client/react";
import { useToastSaver } from "@ui-components/hooks/useToastSaver";
import { useCollectionListener } from "@domain/context/hooks/useCollectionListener";
import { ProductAccessUtils } from "@domain/models/utils/ProductAccessUtils";
import { uuidv7 } from "uuidv7";

export interface PaymentsCtxData {
    list: Payment[];
    map: Map<string, Payment>;
    recordRelation: Map<string, Payment[]>;
    isLoaded: boolean;
}

const defaultPaymentData: PaymentsCtxData = { list: [], map: new Map(), recordRelation: new Map(), isLoaded: false };
export const PaymentsContext = createContext<PaymentsCtxData>(defaultPaymentData);

type PaymentController = {
    onPaymentGql: (v: Omit<GQL.IPaymentsSaveInput, "companyId">, hideMessages?: boolean) => Promise<GQL.ISaveSummary>;
    // FIBU-407: dry-run preflight - returns the prospective summary + approval verdict without
    // committing. No idempotency key (a preview is not a committed intent), no toast.
    onPaymentPreflight: (v: Omit<GQL.IPaymentSavePreflightInput, "companyId">) => Promise<GQL.IPaymentSavePreflight>;
};
export const PaymentsControlContext = createContext<PaymentController>({
    onPaymentGql: (v: Omit<GQL.IPaymentsSaveInput, "companyId">, hideMessages?: boolean) =>
        Promise.reject(hideMessages),
    onPaymentPreflight: () => Promise.reject(new Error("PaymentsControlContext not provided")),
});

export const getPaymentsCtxData = (list: Payment[], isLoaded?: boolean): PaymentsCtxData => {
    const map = new Map<string, Payment>();
    const recordRelation = new Map<string, Payment[]>();
    list.forEach((payment) => {
        map.set(payment.key, payment);
        payment.getRecordRelationKeys().forEach((pk) => {
            if (!recordRelation.has(pk)) {
                recordRelation.set(pk, []);
            }
            recordRelation.get(pk).push(payment);
        });
    });
    return { recordRelation, list, map, isLoaded };
};

export const PaymentsProvider: FC<PropsWithChildren> = (props) => {
    const [payments, setPayments] = useState<PaymentsCtxData>(defaultPaymentData);
    const { companyGQL } = use(CompanyContext);
    const toastSaver = useToastSaver();
    const [savePayments] = useMutation(paymentsSave);
    const [savePaymentsPreflight] = useMutation(paymentsSavePreflight);
    const companyId = companyGQL?.id;

    const shouldSkipLoad = useMemo(() => !ProductAccessUtils.hasCompanyAccountingAccess(companyGQL), [companyGQL]);
    const ref = useMemo(() => child(refPayments, String(companyId)), [companyId]);
    const initializer = useCallback((snap: DataSnapshot) => readFirebasePayment(snap.key, snap.val()), []);

    const { list: paymentsList, isLoaded } = useCollectionListener(ref, initializer, shouldSkipLoad);

    useEffect(() => {
        setPayments(getPaymentsCtxData(paymentsList, isLoaded));
    }, [paymentsList, isLoaded]);

    useEffect(() => {
        if (isLoaded) {
            logDatasetSize("PaymentsContext.payments", { type: "list", list: paymentsList });
        }
    }, [paymentsList, isLoaded]);

    const paymentControl: PaymentController = useMemo(
        () => ({
            onPaymentGql: (v: Omit<GQL.IPaymentsSaveInput, "companyId">, hideMessages?: boolean) => {
                const input: GQL.IPaymentsSaveInput = {
                    ...v,
                    companyId,
                };
                const idempotencyKey = uuidv7();
                const mutationCall = () =>
                    savePayments({
                        variables: { input },
                        context: { idempotencyKey },
                    });
                if (hideMessages) {
                    return mutationCall().then((res) => res.data.paymentsSave);
                }
                return toastSaver(mutationCall).then((res) => res.data.paymentsSave);
            },
            onPaymentPreflight: (v: Omit<GQL.IPaymentSavePreflightInput, "companyId">) => {
                const input: GQL.IPaymentSavePreflightInput = { ...v, companyId };
                // No toastSaver here: the caller needs the returned verdict, and toastSaver swallows
                // errors into `undefined`. The caller (usePaymentModal) try/catches and toasts instead.
                return savePaymentsPreflight({ variables: { input } }).then((res) => {
                    if (!res.data) {
                        throw new Error("Payment preflight returned no data");
                    }
                    return res.data.paymentsSavePreflight;
                });
            },
        }),
        [savePayments, savePaymentsPreflight, toastSaver, companyId],
    );
    return (
        <PaymentsContext value={payments}>
            <PaymentsControlContext value={paymentControl}>{props.children}</PaymentsControlContext>
        </PaymentsContext>
    );
};
